// ─────────────────────────────────────────────
//  providerMap — pure logic for the admin Provider Map (Setup ▸ Provider Map).
//  Facility pins, drive-time → radius conversion, dot colors, and which staff
//  are mappable. The Leaflet wiring lives in pages/setupPage.map.js.
// ─────────────────────────────────────────────

// The three campuses, pinned by street address. Coordinates are the hospital
// main entrances (close enough for a 10-mile-scale staffing map).
//
// MULTI-TENANT NOTE: this is THIS DEPLOYMENT'S SEED, not a source of truth —
// the same class of hardcode as KNOWN_FACILITIES (domain/facilities.js). A
// customer that is not AdventHealth has entirely different coordinates and no
// entry here at all, so their geofence would simply be wrong. Anything that
// must work for them reads the live `facilities/{id}.geofence` map first and
// treats this list as a fallback of last resort (see resolveGeofence).
export const FACILITY_PINS = [
  {
    id: "default",
    label: "AdventHealth Wesley Chapel",
    address: "2600 Bruce B. Downs Blvd, Wesley Chapel, FL 33544",
    lat: 28.19046,
    lng: -82.35465,
  },
  {
    id: "tampa",
    label: "AdventHealth Tampa",
    address: "3100 E Fletcher Ave, Tampa, FL 33613",
    lat: 28.06889,
    lng: -82.42672,
  },
  {
    id: "carrollwood",
    label: "AdventHealth Carrollwood",
    address: "7171 N Dale Mabry Hwy, Tampa, FL 33614",
    lat: 28.01915,
    lng: -82.50528,
  },
];

export const DEFAULT_RADIUS_MINUTES = 30;

// Straight-line radius for a drive-time ring. Suburban Tampa averages about
// 45 km/h door-to-door; a crow-flies circle at that speed slightly OVERSTATES
// reach, which is the safe direction for "who can get here in N minutes".
export const AVG_DRIVE_KMH = 45;

export function minutesToRadiusMeters(minutes, kmh = AVG_DRIVE_KMH) {
  const min = Number(minutes);
  if (!Number.isFinite(min) || min <= 0) return 0;
  return (min / 60) * kmh * 1000;
}

// ─── Geofencing (Board Manager shared devices) ───
// A boardmanager login is a wall device that must physically sit at its
// facility. Radius is generous (campus + parking) because browser geolocation
// on a TV stick can be WiFi-triangulated and hundreds of meters off.
export const GEOFENCE_RADIUS_M = 1500;

// Great-circle distance in meters (haversine).
export function distanceMeters(lat1, lng1, lat2, lng2) {
  // Number(null) is 0 — an easy silent equator bug — so null/'' are invalid too.
  const num = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));
  const a1 = num(lat1), o1 = num(lng1), a2 = num(lat2), o2 = num(lng2);
  if (![a1, o1, a2, o2].every(Number.isFinite)) return NaN;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(a2 - a1);
  const dLng = toRad(o2 - o1);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a1)) * Math.cos(toRad(a2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function facilityPinById(facilityId) {
  return FACILITY_PINS.find((p) => p.id === facilityId) || null;
}

// Strict numeric parse for stored coordinates.
//
// WHY THIS IS NOT `Number(v)`: Number(null), Number(""), Number("  "),
// Number([]) and Number(false) are ALL 0, and 0 is a perfectly valid latitude
// and longitude — (0, 0) is open water in the Gulf of Guinea. A facility whose
// geofence lost a coordinate would therefore be silently relocated to the
// Atlantic and reject every device that is actually standing in the hospital.
// This codebase has already paid for that bug class once (see distanceMeters),
// so absent/blank/non-numeric input yields NaN and the caller fails closed to
// "no pin" — never to a zero coordinate.
function strictNum(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : NaN;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
}

// Validate a stored `facilities/{id}.geofence` map → { lat, lng, radiusMeters,
// address } or null. Null means "this facility has no usable geofence", which
// the caller must treat as unconfigured, NOT as a fence at the equator.
//
// An ABSENT radius means "not configured" and takes this deployment's default;
// a PRESENT but nonsense radius (0, negative, unparseable) rejects the whole
// geofence, because a fence of zero size would lock the facility's own staff
// out of their own board.
export function parseGeofence(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const lat = strictNum(raw.lat);
  const lng = strictNum(raw.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90) return null;
  if (lng < -180 || lng > 180) return null;

  let radiusMeters = GEOFENCE_RADIUS_M;
  const rawRadius = raw.radiusMeters;
  const radiusStored =
    rawRadius !== undefined && rawRadius !== null && rawRadius !== "";
  if (radiusStored) {
    const r = strictNum(rawRadius);
    if (!Number.isFinite(r) || r <= 0) return null;
    radiusMeters = r;
  }
  const address = typeof raw.address === "string" ? raw.address : "";
  return { lat, lng, radiusMeters, address };
}

// Where a facility's geofence comes from — and, just as importantly, WHY there
// isn't one. Resolution order:
//   1. "facility"     — the live facilities/{id}.geofence map (any tenant)
//   2. "builtin"      — FACILITY_PINS, THIS deployment's fallback only
//   3. "unconfigured" — no pin; the caller must say so rather than pretend
//                       the device passed a check that never ran.
export function resolveGeofence(facilityId, facility) {
  const fromDoc = parseGeofence(facility && facility.geofence);
  if (fromDoc) {
    return {
      source: "facility",
      pin: {
        id: facilityId,
        label: (facility && (facility.name || facility.label)) || facilityId,
        ...fromDoc,
      },
    };
  }
  const builtin = facilityPinById(facilityId);
  if (builtin) {
    return { source: "builtin", pin: { ...builtin, radiusMeters: GEOFENCE_RADIUS_M } };
  }
  return { source: "unconfigured", pin: null };
}

// Is a device position inside the facility's geofence?
//  { ok:true,  reason:"inside" }                — verifiably on-site
//  { ok:false, reason:"outside" }               — verifiably off-site
//  { ok:false, reason:"unresolvable-position" } — device coords are garbage
//  { ok:true,  reason:"no-geofence-configured" }— nothing to check against
//
// "No pin means ALLOW" is deliberate and unchanged: a fresh tenant has no
// coordinates on day one, and a board that refuses to load is worse than one
// that loads off-site (the fence is a deterrent — the real protection is the
// facility scoping in firestore.rules). What DID change is that the pass is no
// longer silent: `source` and `reason` say the check never ran, so the UI can
// show it and support can see it instead of reading a pass as a verified
// on-site device.
//
// `facility` is the live facilities/{facilityId} doc (may be null while the
// listener is still resolving). `radiusM` stays an explicit override; when it
// is omitted the radius comes from the resolved pin.
export function checkGeofence(facilityId, lat, lng, radiusM, facility) {
  const { pin, source } = resolveGeofence(facilityId, facility);
  const override = strictNum(radiusM);
  const radius = Number.isFinite(override) && override > 0
    ? override
    : (pin ? pin.radiusMeters : GEOFENCE_RADIUS_M);

  if (!pin) {
    return { ok: true, distance: null, radius, source, reason: "no-geofence-configured" };
  }
  const distance = distanceMeters(lat, lng, pin.lat, pin.lng);
  if (!Number.isFinite(distance)) {
    return { ok: false, distance: null, radius, source, reason: "unresolvable-position" };
  }
  const ok = distance <= radius;
  return { ok, distance, radius, source, reason: ok ? "inside" : "outside" };
}

// Dot colors by staff role: doctors red, CRNAs green, other AHPs teal.
export const DOT_COLORS = { doctor: "#dc2626", crna: "#16a34a", other: "#0e7490" };

export function providerDotColor(member) {
  const role = String(member?.role || "").toLowerCase();
  if (role === "doctor" || role === "anesthesiologist") return DOT_COLORS.doctor;
  if (role === "crna" || member?.subRole === "crna") return DOT_COLORS.crna;
  return DOT_COLORS.other;
}

const validCoord = (v) => Number.isFinite(Number(v));

// Staff that can be drawn: they carry a geocoded home location.
export function mappableStaff(staff) {
  return (Array.isArray(staff) ? staff : []).filter(
    (m) => m && m.geo && validCoord(m.geo.lat) && validCoord(m.geo.lng)
  );
}

// Staff with a street address but no geocode yet — the "Locate on map" queue.
export function ungeocodedStaff(staff) {
  return (Array.isArray(staff) ? staff : []).filter(
    (m) => m && String(m.address || "").trim() && !(m.geo && validCoord(m.geo.lat))
  );
}
